import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import axios from "axios";

import { IPageMeta, IProduct } from "@/services/interfaces/";

import { MetaDefault } from "@/store/catalog/enums";
import { personalModule } from "@/plugins/store-accessors";
import { endpoints } from "@/services/consts";
import { newSaleChecker } from "@/services/api/utils/newSaleChecker";

let cancel: any;
export const saleRoutes = ["sale", "sale-detail", "sale-new"];
export const saleNewRoute = "sale-new";

interface Quantity {
  [key: number]: number;
}

type ExtendedMeta = IPageMeta & { saleCheckboxDisabled?: boolean };

@Module({
  name: "catalog",
  namespaced: true,
})
export default class CatalogModule extends VuexModule {
  quantity: Quantity = {};
  upperCategories = [];
  categories = [];
  products = [];
  meta: ExtendedMeta = MetaDefault;
  pending: Promise<any> | null = null;
  fetching = false;
  error: number | null = null;

  get getUpperCategories() {
    return this.upperCategories;
  }
  get getCategories() {
    return this.categories;
  }
  get getProducts() {
    return this.products;
  }
  get getQuantity() {
    return this.quantity;
  }
  get getMeta() {
    return this.meta;
  }
  get getFetching() {
    return this.fetching;
  }
  get getError() {
    return this.error;
  }

  @Mutation
  setPageMeta(meta: ExtendedMeta = {}) {
    this.meta = Object.assign({}, MetaDefault, meta);
  }

  @Mutation
  setProducts(data: any) {
    this.products = data;
  }

  @Mutation
  setQuantity(data: any) {
    this.quantity = data;
  }

  @Mutation
  changeQuantity({ val, id }: { id: number; val: number }) {
    const quantity = Object.assign({}, this.quantity);
    quantity[id] = val;
    this.quantity = quantity;
  }

  @Mutation
  setBreadCrumbCategories(data: any) {
    this.upperCategories = data;
  }

  @Mutation
  setCategories(data: any) {
    this.categories = data;
  }

  @Mutation
  setRequest(data: any) {
    this.pending = data;
  }

  @Mutation
  setFetching(data: boolean) {
    this.fetching = data;
  }

  @Mutation
  setPageError(data: number = 0) {
    this.error = data;
  }

  @Action
  async getCatalog(params: any): Promise<void> {
    try {
      // console.warn(params);

      this.setPageError();

      setTimeout(() => {
        if (this.pending && !this.error) {
          this.setFetching(true);
        }
      }, 500);

      if (this.pending) {
        cancel("Request was canceled due to incoming request");
        this.setRequest(null);
      }

      let url;

      switch (params.route) {
        case "catalog":
          url = endpoints.catalog.index;
          break;
        case "catalog-detail":
          url = endpoints.catalog.detail + params.pathMatch;
          break;
        case "top":
          url = endpoints.top.index;
          break;
        case "top-detail":
          url = endpoints.top.detail + params.pathMatch;
          break;
        case "sale":
          url = endpoints.sale.index;
          break;
        case "sale-detail":
          url = endpoints.sale.detail + params.pathMatch;
          break;
        case "sale-new":
          url = endpoints.sale.new.index;
          break;
        case "sale-new-detail":
          url = endpoints.sale.new.detail + params.pathMatch;
          break;
        case "promo":
          url = endpoints.promo.index;
          break;
        case "promo-detail":
          url = endpoints.promo.detail + params.pathMatch;
          break;
        default:
          break;
      }

      this.setRequest(
        axios({
          method: "GET",
          url,
          params: params.query,
          cancelToken: new axios.CancelToken(function executor(c: any) {
            cancel = c;
          }),
        }),
      );

      const res = await this.pending;
      this.setRequest(null);

      if (!res) return;
      if (res.status === 401) {
        personalModule.forceLogout();
        return;
      }
      const { data, meta } = res.data;
      if (data.upper_categories) {
        this.setBreadCrumbCategories(data.upper_categories);
      }
      if (data.categories) {
        this.setCategories(data.categories);
      }
      if (data.products) {
        // if (data.products.length) data.products[0] = { ...data.products[0], price: "1400", promo_price: "1200", promo_stock: 4, stock: 20, promo_end: "2023-04-30 06:29:00", promo_start: "2023-04-10 06:29:00" }
        this.setProducts(data.products);
      }
      newSaleChecker(meta);
      this.setPageMeta(meta || {});
      // @ts-ignore-line
    } catch (error: any) {
      console.warn(error);
      this.setPageError(error.response?.status);
    } finally {
      this.setFetching(false);
    }
  }

  @Action
  resetStore() {
    this.setPageMeta();
    this.setProducts([]);
    this.setCategories([]);
    this.setBreadCrumbCategories([]);
    this.setPageError();
  }
}
